import { toast } from 'sonner';

import {
    formatMoney,
    getSupplierName,
} from './formatters';
import type {
    Cart,
    CartItem,
} from './types';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { usePriceVisible } from '@/components/ui/price';
import { Spinner } from '@/components/ui/spinner';

interface CartOrderSummaryProps {
    cart: Cart | null;
    items: CartItem[];
    connectionsLoaded: boolean;
    isSupplierConnected: boolean;
    isCheckoutPending: boolean;
    onPlaceOrder: () => void;
    onContinueShopping: () => void;
    canPlaceOrder?: boolean;
}

export function CartOrderSummary({
    cart,
    items,
    connectionsLoaded,
    isSupplierConnected,
    isCheckoutPending,
    onPlaceOrder,
    onContinueShopping,
    canPlaceOrder = true,
}: CartOrderSummaryProps) {
    const priceVisible = usePriceVisible();
    const handlePlaceOrder = () => {
        if (!canPlaceOrder) {
            toast.error("You don't have permission to place orders.");
            return;
        }
        if (!isSupplierConnected) {
            toast.error(
                'This supplier is no longer a related party. Reconnect to place this order.',
            );
            return;
        }
        onPlaceOrder();
    };

    return (
        <div className="lg:col-span-1">
            <Card className="sticky top-4 border-0 shadow-sm">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {priceVisible && (
                        <>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        Subtotal (
                                        {items.reduce((sum, item) => sum + item.quantity, 0)}
                                        {' '}
                                        items)
                                    </span>
                                    <span className="font-medium">
                                        Rs.
                                        {' '}
                                        {formatMoney(cart?.subtotal)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Discount</span>
                                    <span className="font-medium text-success">
                                        Rs.
                                        {' '}
                                        {formatMoney(cart?.discount_total)}
                                    </span>
                                </div>
                            </div>

                            <div className="border-t border-border pt-3">
                                <div className="flex justify-between">
                                    <span className="font-semibold">Grand Total</span>
                                    <span className="text-lg font-bold">
                                        Rs.
                                        {' '}
                                        {formatMoney(cart?.grand_total)}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}

                    {connectionsLoaded && !isSupplierConnected && (
                        <div
                            data-testid="supplier-disconnected-banner"
                            className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                        >
                            {getSupplierName(cart?.supplier)}
                            {' '}
                            is no longer a related party. Reconnect to place this order.
                        </div>
                    )}

                    {!canPlaceOrder && (
                        <p className="text-xs text-muted-foreground">
                            You don&apos;t have permission to place orders.
                        </p>
                    )}

                    <Button
                        className="w-full"
                        size="lg"
                        onClick={handlePlaceOrder}
                        disabled={
                            !canPlaceOrder
                            || isCheckoutPending
                            || (connectionsLoaded && !isSupplierConnected)
                        }
                    >
                        {isCheckoutPending && (
                            <Spinner className="mr-2 h-4 w-4" />
                        )}
                        Place Order
                    </Button>

                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={onContinueShopping}
                    >
                        Continue Shopping
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
